// Sheet title block (cartiglio). Drawn in paper space on every print sheet from
// a Cartiglio template: a flat list of free-form title objects (bordered rects
// with a wrapped title word and an optional dynamic value / literal body),
// bound to the sheet's paper + orientation.

import type {
  Cartiglio,
  CartiglioSource,
  DrawingMeta,
  FloorKind,
  Lang,
  Revision,
  Sheet,
  SheetViewport,
  TextI18n,
} from '../shared/types';
import type { PaperView } from './sheets';
import { scaleLabel } from './sheets';
import { STENAL_LOGO_ASPECT } from './logo';
import { drawFloorSwatch } from './floorHatch';
import type { LightSymbol } from './lights';
import { paintLightSymbol } from './tagPaint';

interface TitleValues { descrizione: string; commessa: string; versione: string; data: string; autore: string; foglio: string; scala: string; }

/** Pick the `lang` variant of a translatable string, falling back to `base`. */
function pickI18n(base: string, m: TextI18n | undefined, lang: Lang): string {
  const t = m?.[lang];
  return t && t.trim() ? t : base;
}

function foglioLabel(sh: Sheet): string { return sh.paper === 'custom' ? 'Custom' : sh.paper; }

/** The sheet's scale: the common viewport scale, "Custom" when they differ, blank when none. */
function scalaLabel(vps: SheetViewport[]): string {
  if (!vps.length) return '';
  const labels = new Set(vps.map((v) => scaleLabel(v.scale)));
  return labels.size === 1 ? [...labels][0] : 'Custom';
}

/** Raw values available to title-block fields, from the drawing + sheet. */
export function titleValues(sh: Sheet, meta: DrawingMeta | undefined): TitleValues {
  return {
    descrizione: (meta?.descrizione || '').trim(),
    commessa: (meta?.commessa || '').trim(),
    versione: (meta?.versione || '').trim(),
    data: (meta?.data || '').trim(),
    autore: (meta?.autore || '').trim(),
    foglio: foglioLabel(sh),
    scala: scalaLabel(sh.viewports),
  };
}

function fieldValue(source: CartiglioSource, literal: string | undefined, v: TitleValues): string {
  if (source === 'literal') return literal || '';
  if (source === 'blank') return '';
  return v[source] || '';
}

function logoAspect(logo: HTMLImageElement | null): number {
  return logo && logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : STENAL_LOGO_ASPECT;
}

/** Word-wrap `text` to lines no wider than `maxW` px (font must already be set).
 *  Explicit `\n`s are kept as hard line breaks, each re-wrapped on its own. */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const lines: string[] = [];
  for (const para of text.split('\n')) {
    let cur = '';
    for (const w of para.split(' ')) {
      const t = cur ? `${cur} ${w}` : w;
      if (cur && ctx.measureText(t).width > maxW) { lines.push(cur); cur = w; }
      else cur = t;
    }
    lines.push(cur);
  }
  return lines;
}

/** Draw the title block for a sheet onto `ctx`, mapped through the PaperView.
 *  `lang` is the drawing's language: field headings and literal/legal bodies are
 *  shown in that language when the title object carries a translation. */
export function drawTitleBlock(
  ctx: CanvasRenderingContext2D,
  pv: PaperView,
  sh: Sheet,
  cart: Cartiglio,
  meta: DrawingMeta | undefined,
  logo: HTMLImageElement | null,
  lang: Lang,
): void {
  drawTitleObjects(ctx, pv, sh, cart, meta, logo, lang);
}

const REV_TABLE_HEADER_MM = 6;
const REV_TABLE_ROW_MM = 5;
const REV_TABLE_COLS: Array<{ frac: number; label: Record<Lang, string> }> = [
  { frac: 0.12, label: { en: 'Rev', it: 'Rev', fr: 'Rév' } },
  { frac: 0.2, label: { en: 'Date', it: 'Data', fr: 'Date' } },
  { frac: 0.5, label: { en: 'Description', it: 'Descrizione', fr: 'Description' } },
  { frac: 0.18, label: { en: 'By', it: 'Autore', fr: 'Par' } },
];

const REV_TABLE_DATA_MM = 2.1;   // data-row font size (mm)
const REV_TABLE_LINE_MM = 2.7;   // wrapped-line spacing (mm)
const REV_TABLE_PAD_MM = 1;      // cell horizontal padding (mm)
const REV_TABLE_VPAD_MM = 1.4;   // cell vertical padding, total (mm)
const REV_DESC_INDEX = 2;        // the wrapping Description column
const REV_TABLE_EMPTY: Record<Lang, string> = { en: '(no revisions)', it: '(nessuna revisione)', fr: '(aucune révision)' };

/** Off-screen context for measuring text at 1 px = 1 mm, so wrapped line counts
 *  (and the row heights derived from them) are computed in mm and independent of
 *  the on-screen paper scale. */
let revMeasureCtx: CanvasRenderingContext2D | null = null;
function revMeasure(): CanvasRenderingContext2D {
  if (!revMeasureCtx) revMeasureCtx = document.createElement('canvas').getContext('2d');
  return revMeasureCtx!;
}

function revTableRows(revisions: Revision[], lang: Lang): Array<Pick<Revision, 'tag' | 'date' | 'description' | 'author'>> {
  return revisions.length ? revisions : [{ tag: '—', date: '', description: REV_TABLE_EMPTY[lang], author: '' }];
}

/** Per-row heights (mm) for a table of the given width: each data row grows to
 *  fit its word-wrapped Description, never shrinking below the base row height. */
function revTableRowHeights(widthMm: number, rows: Array<{ description: string }>): number[] {
  const ctx = revMeasure();
  ctx.font = `${REV_TABLE_DATA_MM}px Arial, sans-serif`;
  const descInner = Math.max(4, widthMm * REV_TABLE_COLS[REV_DESC_INDEX].frac - REV_TABLE_PAD_MM * 2);
  return rows.map((rev) => {
    const lines = Math.max(1, wrapLines(ctx, rev.description || '', descInner).length);
    return Math.max(REV_TABLE_ROW_MM, lines * REV_TABLE_LINE_MM + REV_TABLE_VPAD_MM);
  });
}

/** The changes table's drawn height (mm) at a given width: a header row plus one
 *  word-wrapped row per revision (at least one placeholder row when empty). */
export function revisionTableHeightMm(widthMm: number, revisions: Revision[], lang: Lang): number {
  return REV_TABLE_HEADER_MM + revTableRowHeights(widthMm, revTableRows(revisions, lang)).reduce((a, b) => a + b, 0);
}

/** Draw the sheet's changes table (a real bordered grid, unlike the free-form
 *  title-object blocks): Rev / Date / Description / By, one row per revision,
 *  oldest first. Width comes from `sh.revisionTableRect` (user-resizable); the
 *  Description column word-wraps and each row grows to fit. Drawing writes
 *  nothing: the height is laid out before (`revisionTableHeightMm`). */
export function drawRevisionTable(ctx: CanvasRenderingContext2D, pv: PaperView, sh: Sheet, revisions: Revision[], lang: Lang): void {
  const rect = sh.revisionTableRect;
  if (!rect) return;
  const s = pv.scale;
  const X = (v: number): number => pv.ox + v * s, Y = (v: number): number => pv.oy + v * s, mm = (v: number): number => v * s;
  const rows = revTableRows(revisions, lang);
  const rowHmm = revTableRowHeights(rect.w, rows);
  const totalMm = REV_TABLE_HEADER_MM + rowHmm.reduce((a, b) => a + b, 0);
  const x = X(rect.x), y = Y(rect.y), w = mm(rect.w);
  const headH = mm(REV_TABLE_HEADER_MM), h = mm(totalMm);

  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y, w, h);
  ctx.lineWidth = Math.max(1, mm(0.3));
  ctx.strokeStyle = '#222222';
  ctx.strokeRect(x, y, w, h);

  let cx = x;
  const colX: number[] = [x];
  for (const col of REV_TABLE_COLS) { cx += w * col.frac; colX.push(cx); }
  const pad = mm(REV_TABLE_PAD_MM), maxW = (i: number): number => colX[i + 1] - colX[i] - pad * 2;

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  // Match the cartiglio title-object scale: bold labels at the 2.2 mm word font,
  // data rows a touch smaller, so the table reads as part of the same sheet.
  ctx.font = `700 ${(2.2 * s).toFixed(1)}px Arial, sans-serif`;
  ctx.fillStyle = '#222222';
  REV_TABLE_COLS.forEach((col, i) => ctx.fillText(col.label[lang], colX[i] + pad, y + headH / 2, maxW(i)));

  ctx.font = `${(REV_TABLE_DATA_MM * s).toFixed(1)}px Arial, sans-serif`;
  const lineH = mm(REV_TABLE_LINE_MM);
  const rowTop: number[] = [];
  let ry = y + headH;
  rows.forEach((rev, r) => {
    rowTop.push(ry);
    const rowH = mm(rowHmm[r]);
    ctx.textBaseline = 'middle';
    [rev.tag, rev.date, '', rev.author || ''].forEach((text, i) => {
      if (i === REV_DESC_INDEX || !text) return;
      ctx.fillText(text, colX[i] + pad, ry + rowH / 2, maxW(i));
    });
    // Description: word-wrapped, the block vertically centred in its row.
    ctx.textBaseline = 'top';
    const dLines = wrapLines(ctx, rev.description || '', maxW(REV_DESC_INDEX));
    let dy = ry + Math.max(mm(0.4), (rowH - dLines.length * lineH) / 2);
    for (const ln of dLines) { ctx.fillText(ln, colX[REV_DESC_INDEX] + pad, dy); dy += lineH; }
    ry += rowH;
  });

  // Grid lines: column separators, then row separators (header + each revision).
  ctx.beginPath();
  for (let i = 1; i < colX.length - 1; i++) { ctx.moveTo(colX[i], y); ctx.lineTo(colX[i], y + h); }
  ctx.moveTo(x, y + headH); ctx.lineTo(x + w, y + headH);
  for (let r = 1; r < rows.length; r++) { ctx.moveTo(x, rowTop[r]); ctx.lineTo(x + w, rowTop[r]); }
  ctx.lineWidth = Math.max(0.6, mm(0.2));
  ctx.stroke();
  ctx.restore();
}

// ---- Paper-space schedule tables (connection / preparation) ----

const SCHED_TITLE_MM = 6;   // title bar height (mm)
const SCHED_HEADER_MM = 5;  // column-header row height (mm)

/** One schedule row for the paper table: the discipline number (drawn in the
 *  discipline colour) plus one string per non-number column (all word-wrap). A
 *  flooring row carries no number — it sets `swatchKind` and the No. column draws
 *  the type's hatch swatch instead, exactly as the model-view schedule does. */
export interface SheetScheduleRow { num: string; numColor: string; cells: string[]; swatchKind?: FloorKind; /** A lighting row: its light's plan symbol, drawn in the first text column. */ symbol?: LightSymbol }

function schedRowHeights(widthMm: number, fracs: number[], rows: SheetScheduleRow[]): number[] {
  const ctx = revMeasure();
  ctx.font = `${REV_TABLE_DATA_MM}px Arial, sans-serif`;
  const innerOf = (i: number): number => Math.max(4, widthMm * fracs[i] - REV_TABLE_PAD_MM * 2);
  // A row grows to fit the tallest of its word-wrapped text columns.
  return rows.map((r) => {
    let lines = 1;
    for (let i = 1; i < fracs.length; i++) lines = Math.max(lines, wrapLines(ctx, r.cells[i - 1] || '', innerOf(i)).length);
    return Math.max(REV_TABLE_ROW_MM, lines * REV_TABLE_LINE_MM + REV_TABLE_VPAD_MM);
  });
}

/** Drawn height (mm) of a schedule table: title bar + header row + word-wrapped
 *  data rows (at least one placeholder row when empty). */
export function scheduleTableHeightMm(widthMm: number, fracs: number[], rows: SheetScheduleRow[]): number {
  const rs = rows.length ? rows : [{ num: '', numColor: '#222', cells: fracs.slice(1).map(() => '') }];
  return SCHED_TITLE_MM + SCHED_HEADER_MM + schedRowHeights(widthMm, fracs, rs).reduce((a, b) => a + b, 0);
}

/** Draw a paper-space schedule table (a bordered grid, transparent background):
 *  a title bar, a column-header row, then one row per schedule entry with the
 *  discipline number in its colour. Width comes from `rect.w` (user-resizable);
 *  every text column word-wraps. Drawing writes nothing: the height is laid out
 *  before (`scheduleTableHeightMm`). */
export function drawScheduleTablePaper(
  ctx: CanvasRenderingContext2D, pv: PaperView, rect: { x: number; y: number; w: number; h: number },
  title: string, headers: string[], fracs: number[], rowsIn: SheetScheduleRow[], emptyLabel: string,
): void {
  const rows: SheetScheduleRow[] = rowsIn.length ? rowsIn : [{ num: '', numColor: '#222', cells: headers.slice(1).map((_, i) => (i === headers.length - 2 ? emptyLabel : '')) }];
  const s = pv.scale;
  const X = (v: number): number => pv.ox + v * s, Y = (v: number): number => pv.oy + v * s, mm = (v: number): number => v * s;
  const rowH = schedRowHeights(rect.w, fracs, rows);
  const total = SCHED_TITLE_MM + SCHED_HEADER_MM + rowH.reduce((a, b) => a + b, 0);
  const x = X(rect.x), y = Y(rect.y), w = mm(rect.w), h = mm(total);
  const titleH = mm(SCHED_TITLE_MM), headH = mm(SCHED_HEADER_MM);

  ctx.save();
  // Transparent background — only the border, grid lines and text are drawn.
  ctx.lineWidth = Math.max(1, mm(0.3)); ctx.strokeStyle = '#222222'; ctx.strokeRect(x, y, w, h);

  let cx = x; const colX: number[] = [x];
  for (const f of fracs) { cx += w * f; colX.push(cx); }
  const pad = mm(REV_TABLE_PAD_MM), maxW = (i: number): number => colX[i + 1] - colX[i] - pad * 2;

  ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillStyle = '#222222';
  ctx.font = `700 ${(2.4 * s).toFixed(1)}px Arial, sans-serif`;
  ctx.fillText(title, x + pad, y + titleH / 2, w - pad * 2);
  ctx.font = `700 ${(2.1 * s).toFixed(1)}px Arial, sans-serif`;
  headers.forEach((hd, i) => ctx.fillText(hd, colX[i] + pad, y + titleH + headH / 2, maxW(i)));

  const lineH = mm(REV_TABLE_LINE_MM);
  const rowTop: number[] = [];
  let ry = y + titleH + headH;
  rows.forEach((r, ri) => {
    rowTop.push(ry);
    const rh = mm(rowH[ri]);
    // No. column: a hatch swatch for a flooring row (it carries no code), else the
    // discipline number (coloured), vertically centred.
    if (r.swatchKind) {
      drawFloorSwatch(ctx, colX[0], ry, colX[1] - colX[0], rh, r.swatchKind);
    } else {
      ctx.textBaseline = 'middle';
      ctx.fillStyle = r.numColor;
      ctx.font = `700 ${(REV_TABLE_DATA_MM * s).toFixed(1)}px Arial, sans-serif`;
      if (r.num) ctx.fillText(r.num, colX[0] + pad, ry + rh / 2, maxW(0));
    }
    // Every text column: word-wrapped, vertically centred in the row.
    ctx.fillStyle = '#222222';
    ctx.font = `${(REV_TABLE_DATA_MM * s).toFixed(1)}px Arial, sans-serif`;
    ctx.textBaseline = 'top';
    for (let i = 1; i < fracs.length; i++) {
      const dLines = wrapLines(ctx, r.cells[i - 1] || '', maxW(i));
      let dy = ry + Math.max(mm(0.4), (rh - dLines.length * lineH) / 2);
      for (const ln of dLines) { ctx.fillText(ln, colX[i] + pad, dy); dy += lineH; }
    }
    if (r.symbol) paintLightSymbol(ctx, r.symbol, { x: (colX[1] + colX[2]) / 2, y: ry + rh / 2 }, (colX[2] - colX[1]) * 0.8, Math.min(rh * 0.7, mm(4)), r.numColor);
    ry += rh;
  });

  ctx.beginPath();
  for (let i = 1; i < colX.length - 1; i++) { ctx.moveTo(colX[i], y + titleH); ctx.lineTo(colX[i], y + h); }
  ctx.moveTo(x, y + titleH); ctx.lineTo(x + w, y + titleH);
  ctx.moveTo(x, y + titleH + headH); ctx.lineTo(x + w, y + titleH + headH);
  for (let r = 1; r < rows.length; r++) { ctx.moveTo(x, rowTop[r]); ctx.lineTo(x + w, rowTop[r]); }
  ctx.lineWidth = Math.max(0.6, mm(0.2));
  ctx.stroke();
  ctx.restore();
}

/** Draw a free-form object-model title block: each object is a bordered rect
 *  with a wrapped title word and (for fields/text) a wrapped dynamic value. */
function drawTitleObjects(
  ctx: CanvasRenderingContext2D, pv: PaperView, sh: Sheet, cart: Cartiglio,
  meta: DrawingMeta | undefined, logo: HTMLImageElement | null, lang: Lang,
): void {
  const s = pv.scale;
  const X = (v: number): number => pv.ox + v * s, Y = (v: number): number => pv.oy + v * s, mm = (v: number): number => v * s;
  const values = titleValues(sh, meta);
  ctx.save();
  for (const o of cart.objects || []) {
    const x = X(o.x), y = Y(o.y), w = mm(o.w), h = mm(o.h);
    // Per-side borders.
    const b = o.border;
    ctx.strokeStyle = b.color;
    const edge = (x1: number, y1: number, x2: number, y2: number, t: number): void => {
      if (t <= 0) return;
      ctx.lineWidth = Math.max(0.6, mm(t));
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    };
    edge(x, y, x + w, y, b.top); edge(x, y + h, x + w, y + h, b.bottom);
    edge(x, y, x, y + h, b.left); edge(x + w, y, x + w, y + h, b.right);

    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    const pad = mm(1.4), innerX = x + pad, innerW = Math.max(2, w - pad * 2), bottom = y + h - pad;
    if (o.role === 'logo') {
      if (logo) {
        const asp = logoAspect(logo);
        let lw = w - pad * 2, lh = lw / asp;
        if (lh > h - pad * 2) { lh = h - pad * 2; lw = lh * asp; }
        ctx.drawImage(logo, x + (w - lw) / 2, y + (h - lh) / 2, lw, lh);
      }
      ctx.restore(); continue;
    }
    ctx.textBaseline = 'top'; ctx.textAlign = 'left';
    let cy = y + pad;
    const word = pickI18n(o.word, o.wordI18n, lang);
    if (word && o.wordFontMm > 0) {
      ctx.fillStyle = o.wordColor;
      ctx.font = `700 ${(o.wordFontMm * s).toFixed(1)}px ${o.wordFont}`;
      const lh = o.wordFontMm * s * 1.25;
      for (const ln of wrapLines(ctx, word, innerW)) { if (cy > bottom) break; ctx.fillText(ln, innerX, cy); cy += lh; }
      cy += lh * 0.2;
    }
    const literal = pickI18n(o.literal || '', o.literalI18n, lang);
    const value = o.role === 'field'
      ? fieldValue(o.source ?? 'blank', literal, values)
      : literal;
    if (value && o.valueFontMm > 0) {
      ctx.fillStyle = o.valueColor;
      ctx.font = `${(o.valueFontMm * s).toFixed(1)}px ${o.valueFont}`;
      const lh = o.valueFontMm * s * 1.28;
      for (const ln of wrapLines(ctx, value, innerW)) { if (cy > bottom) break; ctx.fillText(ln, innerX, cy); cy += lh; }
    }
    ctx.restore();
  }
  ctx.restore();
}

